import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  clientsApi, contactsApi, devicesApi, gatewaysApi, callbacksApi, mqttApi, telemetryApi,
} from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { StatusDot, Mono } from "@/components/Atoms";
import { Users, Contact as ContactIcon, Cpu, Radio, Webhook, ArrowUpRight } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip,
} from "recharts";
import { useCan } from "@/lib/rbac";

// Dashboard fires many parallel API calls to build the KPI/summary widgets.
// Each of those calls must be gated on the current role's read capabilities,
// otherwise operators/viewers pile up 403 toasts on page load (contacts,
// callbacks, mqtt, telemetry, gateway/messages all forbidden for operator).
const useStats = ({ canReadContacts, canReadCallbacks }) => {
  return useQuery({
    queryKey: ["dashboard-stats", canReadContacts, canReadCallbacks],
    queryFn: async () => {
      const [c, ct, d, g, cb] = await Promise.all([
        clientsApi.list(),
        canReadContacts ? contactsApi.list().catch(() => []) : Promise.resolve([]),
        devicesApi.list(),
        gatewaysApi.list(),
        canReadCallbacks ? callbacksApi.list().catch(() => []) : Promise.resolve([]),
      ]);
      return {
        clients: c.length,
        contacts: ct.length,
        devices: d.length,
        gateways: g.length,
        callbacks: cb.filter((x) => x.enabled).length,
      };
    },
  });
};

const formatBucketLabel = (iso, bucket_size) => {
  const d = new Date(iso);
  if (bucket_size === "1d" || bucket_size === "6h") {
    return `${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCDate()).padStart(2, "0")}`;
  }
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const KpiCard = ({ to, icon: Icon, label, value, hint, testid }) => (
  <Link
    to={to}
    className="bg-white border border-zinc-200 rounded-lg p-5 hover:border-zinc-900 hover:shadow-sm transition group"
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        {label}
      </div>
      <Icon className="w-4 h-4 text-zinc-400 group-hover:text-zinc-900 transition" strokeWidth={1.5} />
    </div>
    <div className="mt-3 flex items-baseline gap-2">
      <div className="font-heading text-3xl font-bold tracking-tight">{value}</div>
      {hint && <div className="text-xs text-lime-600 font-mono">{hint}</div>}
    </div>
    <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-400 font-mono inline-flex items-center gap-1 group-hover:text-zinc-900 transition">
      view all <ArrowUpRight className="w-3 h-3" />
    </div>
  </Link>
);

export default function Dashboard() {
  // Cache the caps up-front so useQuery's `enabled` predicate is deterministic.
  const canReadContacts  = useCan("read:contacts");
  const canReadCallbacks = useCan("read:callbacks");
  const canReadMqtt      = useCan("read:mqtt");
  const canReadTelemetry = useCan("read:telemetry");

  const stats = useStats({ canReadContacts, canReadCallbacks });
  const topics = useQuery({
    queryKey: ["mqtt-topics"],
    queryFn: () => mqttApi.list(),
    enabled: canReadMqtt,
  });

  const telemetry = useQuery({
    queryKey: ["telemetry-timeseries", "24h"],
    queryFn: () => telemetryApi.timeseries({ window: "24h" }),
    retry: 0,
    enabled: canReadTelemetry,
  });

  // Drop the trailing bucket if it represents the current (incomplete) clock
  // hour. Backend buckets by clock hour, so the "now" bucket only contains a
  // partial sample and visually dives to zero. Keep at least 1 point.
  const rawPoints = telemetry.data?.points || [];
  const trimmedPoints = (() => {
    if (rawPoints.length < 2) return rawPoints;
    const last = rawPoints[rawPoints.length - 1];
    const lastBucket = new Date(last.bucket).getTime();
    const nowBucket = Math.floor(Date.now() / 3600000) * 3600000;
    return lastBucket >= nowBucket ? rawPoints.slice(0, -1) : rawPoints;
  })();
  const chartData = trimmedPoints.map((p) => ({
    hour: formatBucketLabel(p.bucket, telemetry.data?.bucket_size),
    messages: p.messages,
  }));
  const chartEmpty = !telemetry.isLoading && chartData.length === 0;

  return (
    <PageContainer>
      <PageHeader
        title="Operations dashboard"
        subtitle="Real-time view across all clients, gateways and parser pipelines."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          to="/clients"
          icon={Users}
          label="Clients"
          value={stats.data?.clients ?? "—"}
          testid="kpi-clients"
        />
        {canReadContacts && (
          <KpiCard
            to="/contacts"
            icon={ContactIcon}
            label="Contacts"
            value={stats.data?.contacts ?? "—"}
            testid="kpi-contacts"
          />
        )}
        <KpiCard
          to="/devices"
          icon={Cpu}
          label="Devices"
          value={stats.data?.devices ?? "—"}
          testid="kpi-devices"
        />
        <KpiCard
          to="/gateways"
          icon={Radio}
          label="Gateways"
          value={stats.data?.gateways ?? "—"}
          testid="kpi-gateways"
        />
        {canReadCallbacks && (
          <KpiCard
            to="/callbacks"
            icon={Webhook}
            label="Active callbacks"
            value={stats.data?.callbacks ?? "—"}
            testid="kpi-callbacks"
          />
        )}
      </div>

      {(canReadTelemetry || canReadMqtt) && (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
        {canReadTelemetry && (
        <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 dark:text-zinc-400 font-mono">
                telemetry · 24h
              </div>
              <div className="font-heading text-base font-semibold mt-0.5">
                Ingest rate (messages / hour)
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-500 font-mono">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#3730A3]" />
                ingest
              </span>
            </div>
          </div>
          <div className="h-[260px]">
            {chartEmpty ? (
              <div className="h-full grid place-items-center text-sm text-zinc-400 dark:text-zinc-500" data-testid="telemetry-empty">
                No telemetry data in the last 24 hours.
              </div>
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3730A3" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#3730A3" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F5" vertical={false} />
                <XAxis
                  dataKey="hour"
                  stroke="#A1A1AA"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis stroke="#A1A1AA" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "#3730A3",
                    border: "1px solid #27272A",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "white",
                    fontFamily: "JetBrains Mono, monospace",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="messages"
                  stroke="#3730A3"
                  strokeWidth={1.75}
                  fill="url(#g1)"
                />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>
        )}

        {canReadMqtt && (
        <div className="bg-white border border-zinc-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              mqtt topics
            </div>
            <ArrowUpRight className="w-3.5 h-3.5 text-zinc-400" />
          </div>
          <div className="space-y-2">
            {topics.data?.slice(0, 6).map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2">
                <Mono className="truncate">{t.topic}</Mono>
                <StatusDot online={t.enabled} label={t.enabled ? "live" : "paused"} />
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
      )}

      <FleetSection />
    </PageContainer>
  );
}
// -----------------------------------------------------------------------------
// Fleet section — universally accessible widgets (device types + per-client
// breakdown). Works for every role since the underlying endpoints
// (`/api/devices`, `/api/devices/types`, `/api/clients`) are readable by
// admin/service/operator/viewer.
// -----------------------------------------------------------------------------
function FleetSection() {
  const navigate = useNavigate();
  const devices = useQuery({ queryKey: ["devices-dash"], queryFn: () => devicesApi.list() });
  const types   = useQuery({ queryKey: ["device-types-dash"], queryFn: devicesApi.types });
  const clients = useQuery({ queryKey: ["clients-dash"], queryFn: clientsApi.list });

  const typeById   = React.useMemo(() => Object.fromEntries((types.data || []).map((t) => [String(t.id), t])), [types.data]);
  const clientById = React.useMemo(() => Object.fromEntries((clients.data || []).map((c) => [String(c.id), c])), [clients.data]);

  const devicesByType = React.useMemo(() => {
    const counts = new Map();
    (devices.data || []).forEach((d) => {
      const key = d.type_id != null ? String(d.type_id) : "__untyped__";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([k, count]) => ({
        type_id: k,
        name: k === "__untyped__" ? "Untyped" : (typeById[k]?.name || `type ${k}`),
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [devices.data, typeById]);

  const devicesPerClient = React.useMemo(() => {
    const counts = new Map();
    (devices.data || []).forEach((d) => {
      const key = d.client_id != null ? String(d.client_id) : "__unassigned__";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([k, count]) => ({
        client_id: k,
        name: k === "__unassigned__" ? "Unassigned" : (clientById[k]?.name || `client ${k}`),
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [devices.data, clientById]);

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Devices by type -------------------------------------------------- */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="dash-devices-by-type">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">breakdown · devices</div>
            <div className="font-heading text-base font-semibold mt-0.5">Devices by type</div>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono hidden sm:inline">
            click bar to filter
          </span>
        </div>
        {devicesByType.length === 0 ? (
          <div className="h-[260px] grid place-items-center text-sm text-zinc-400">No devices yet.</div>
        ) : (
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={devicesByType} layout="vertical" margin={{ top: 4, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F5" horizontal={false} />
                <XAxis type="number" stroke="#A1A1AA" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} width={110} />
                <Tooltip
                  contentStyle={{
                    background: "#3730A3",
                    border: "1px solid #27272A",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "white",
                    fontFamily: "JetBrains Mono, monospace",
                  }}
                  cursor={{ fill: "#F4F4F5" }}
                />
                <Bar
                  dataKey="count"
                  fill="#3730A3"
                  radius={[0, 4, 4, 0]}
                  className="cursor-pointer"
                  onClick={(payload) => {
                    // Recharts passes the datum directly on <Bar> click.
                    const tid = payload?.type_id ?? payload?.payload?.type_id;
                    if (!tid || tid === "__untyped__") {
                      navigate("/devices");
                    } else {
                      navigate(`/devices?type=${encodeURIComponent(tid)}`);
                    }
                  }}
                  data-testid="dash-bar-devices-by-type"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Devices per client ---------------------------------------------- */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="dash-devices-per-client">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">fleet · per client</div>
            <div className="font-heading text-base font-semibold mt-0.5">Devices per client</div>
          </div>
          <Link to="/clients" className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-900 font-mono inline-flex items-center gap-1">
            view clients <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        {devicesPerClient.length === 0 ? (
          <div className="text-sm text-zinc-400 py-6 text-center">No devices assigned.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {devicesPerClient.map((c) => (
              <Link
                key={c.client_id}
                to={c.client_id !== "__unassigned__" ? `/devices?client=${encodeURIComponent(c.client_id)}` : "/devices"}
                className="border border-zinc-200 rounded-md px-4 py-3 hover:border-zinc-900 hover:bg-zinc-50 transition group"
                data-testid={`dash-client-tile-${c.client_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs text-zinc-500 truncate">{c.name}</div>
                  <ArrowUpRight className="w-3 h-3 text-zinc-300 group-hover:text-zinc-900 transition" />
                </div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="font-heading text-2xl font-bold tracking-tight">{c.count}</span>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">devices</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


